import AutoAwesomeOutlined from '@mui/icons-material/AutoAwesomeOutlined';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { Pill, PRIMARY_PILL_SX } from './Pill.js';
import type { ReportRecommendation, ReportTile } from './reportSummary.js';

const TILE_MIN_WIDTH = 180;
const TILE_COLUMNS = `repeat(auto-fit, minmax(${TILE_MIN_WIDTH}px, 1fr))`;

interface ReportHeadlineProps {
  headline: string;
  detail: string | null;
  tiles: ReportTile[];
}

export function ReportHeadline({ headline, detail, tiles }: ReportHeadlineProps): ReactNode {
  return (
    <Stack sx={{ gap: 3 }}>
      <Stack
        direction="row"
        sx={{ gap: 4, alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap' }}
      >
        <Typography
          data-testid="report-headline"
          sx={{
            fontSize: 12,
            fontWeight: 700,
            textTransform: 'uppercase',
            color: 'error.main',
          }}
        >
          {headline}
        </Typography>
        {detail === null ? null : (
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{detail}</Typography>
        )}
      </Stack>

      <Box sx={{ display: 'grid', gridTemplateColumns: TILE_COLUMNS, gap: 3 }}>
        {tiles.map((tile) => (
          <Stack
            key={tile.label}
            data-testid={`report-tile-${tile.label}`}
            sx={{
              gap: 0.5,
              px: 4,
              py: 3,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
            }}
          >
            <Typography
              sx={{
                fontSize: 22,
                fontWeight: 600,
                color: tile.negative === true ? 'error.main' : 'text.primary',
              }}
            >
              {tile.value}
            </Typography>
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{tile.label}</Typography>
          </Stack>
        ))}
      </Box>
    </Stack>
  );
}

export function ReportSection({ title }: { title: string }): ReactNode {
  return (
    <Stack direction="row" sx={{ gap: 1.5, alignItems: 'center' }}>
      <AutoAwesomeOutlined sx={{ fontSize: 15, color: 'primary.main' }} />
      <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{title}</Typography>
    </Stack>
  );
}

export function ReportRecommendationLine({
  recommendation,
}: {
  recommendation: ReportRecommendation;
}): ReactNode {
  return (
    <Stack sx={{ gap: 0.5, minWidth: 0 }}>
      <Stack direction="row" sx={{ gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{recommendation.title}</Typography>
        <Pill sx={PRIMARY_PILL_SX}>RECOMMENDED</Pill>
      </Stack>
      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
        {recommendation.detail}
      </Typography>
    </Stack>
  );
}
